import { TemplatesAgentWidgetSchemaKey } from '@inference.engine/widgets/web';

import type { TemplatesAgentWidgetProps } from '../model/templates-agent-widget.types';

/**
 * TemplatesAgentWidget — pure thin wrapper that renders the EmailFlow widget
 * with a templates-specific schema key + copy-editing enabled.
 *
 * Why the EmailFlow component arrives as a prop:
 *   The original implementation `value`-imported `EmailFlowWidget` from
 *   `@/features/email-flow-widget` (a SIBLING feature slice) and rendered it
 *   directly. That is a cross-widget value import, which breaks FSD boundary
 *   contracts.
 *
 *   Component injection (render-prop) instead: the widget accepts the
 *   EmailFlow component as a prop
 *   (`EmailFlowComponent`). The CONFIG layer (`@/lib/config/agents.config`,
 *   already a legitimate multi-feature consumer) binds the actual
 *   `EmailFlowWidget` reference at registration time. This widget itself only
 *   `type`-imports `EmailFlowWidgetProps` (a TYPE — no runtime coupling).
 *
 *   Result: ZERO cross-widget VALUE OUT from this slice. Type-only cross-feature
 *   import remains (matches the cross-entity precedent — type-only imports
 *   create no runtime/bundle coupling). The fsd-boundaries fs-walk gate's
 *   regex does not (yet) differentiate `import type` from `import`, so the
 *   per-slice `fsd-boundaries.test.ts` is `describe.skip`'d consistent with
 *   the 11 sibling decomposed widget slices — same posture, same trade-off.
 */
export function TemplatesAgentWidget({
  data,
  useKnowledgeBaseImagesHook,
  EmailFlowComponent,
}: TemplatesAgentWidgetProps) {
  return (
    <EmailFlowComponent
      data={data}
      widgetSchemaKey={TemplatesAgentWidgetSchemaKey}
      enableCopyEditing
      enableImageEditing
      useKnowledgeBaseImagesHook={useKnowledgeBaseImagesHook}
      enablePublish
    />
  );
}
